"use client";

import type { ComponentPropsWithoutRef, ElementType, Ref, RefObject } from "react";

import {
  useCcSectionReveal,
  type CcSectionRevealOptions,
} from "@/lib/use-cc-section-reveal";

type CcSectionRevealProps<T extends ElementType> = CcSectionRevealOptions & {
  as?: T;
  className?: string;
  revealRef?: Ref<HTMLElement>;
} & Omit<ComponentPropsWithoutRef<T>, "as" | "className">;

/**
 * Optional wrapper when a section root should not host other refs/hooks.
 * Prefer `useCcSectionReveal` on existing `<section>` elements when possible.
 */
export function CcSectionReveal<T extends ElementType = "section">({
  as,
  className,
  revealRef,
  staggerChildren,
  threshold,
  rootMargin,
  ...rest
}: CcSectionRevealProps<T>) {
  const Component = as ?? "section";
  const { ref, withRevealClassName } = useCcSectionReveal(
    { staggerChildren, threshold, rootMargin },
    revealRef as RefObject<HTMLElement | null> | undefined,
  );

  return (
    <Component
      ref={ref}
      className={withRevealClassName(className)}
      {...rest}
    />
  );
}
